import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowLeft, Download, Smartphone, Droplets, AlertTriangle, 
  Pill, Phone, User, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import html2canvas from 'html2canvas';
import { useDemo } from '@/contexts/DemoContext';

/**
 * LockScreenCardPage - Generate emergency card for phone lock screen
 * Large text, high contrast, simple layout
 */

const LockScreenCardPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const { isDemoMode, getProfiles, getMedicalProfile, getAllergies, getMedications, getEmergencyContacts } = useDemo();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchData();
  }, [profileId, isDemoMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Demo mode: build summary from local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const medicalProfile = getMedicalProfile() || {};
        const allergies = getAllergies() || [];
        const medications = getMedications() || [];
        const emergencyContacts = getEmergencyContacts() || [];
        const primaryEmergencyContact = emergencyContacts[0];
        
        setProfile({
          name: demoProfile?.name || 'Demo User',
          medical_info: {
            full_name: medicalProfile.full_name || demoProfile?.name,
            date_of_birth: medicalProfile.date_of_birth || ''
          }
        });
        
        setSummary({
          blood_type: medicalProfile.blood_type || '',
          allergies: allergies.map(a => a.name),
          medications: medications.map(m => m.name),
          emergency_contact: primaryEmergencyContact?.name || '',
          emergency_contact_relationship: primaryEmergencyContact?.relationship || '',
          emergency_contact_phone: primaryEmergencyContact?.phone || ''
        });
        
        setLoading(false);
        return;
      }
      
      const [profileRes, summaryRes] = await Promise.all([
        API.get(`/emergency-binder/profiles/${profileId}`),
        API.get(`/emergency-binder/profiles/${profileId}/emergency-summary`)
      ]);
      setProfile(profileRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    try {
      setGenerating(true);
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#1F2937',
        useCORS: true,
      });
      
      const link = document.createElement('a');
      link.download = `emergency-card-${profile?.name || 'card'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Emergency card downloaded!');
    } catch (error) {
      console.error('Failed to generate card:', error);
      toast.error('Failed to generate card');
    } finally {
      setGenerating(false);
    }
  };

  const medicalInfo = profile?.medical_info || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 pb-24" data-testid="lock-screen-card-page">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/emergency-binder')}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-white">Lock Screen Card</h1>
              <p className="text-xs text-slate-400">{profile?.name}</p>
            </div>
            <Button
              onClick={handleDownload}
              disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-card-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {generating ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Instructions */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Smartphone className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-white mb-1">How to use</h3>
                <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                  <li>Download the emergency card image below</li>
                  <li>Set it as your phone's lock screen wallpaper</li>
                  <li>First responders can see critical info without unlocking</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Card Preview */}
        <div className="flex justify-center">
          <div 
            ref={cardRef}
            className="w-[375px] min-h-[667px] bg-gray-800 rounded-3xl p-6 shadow-2xl"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            {/* Card Header */}
            <div className="text-center mb-6 pt-8">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                <AlertTriangle className="h-4 w-4" />
                EMERGENCY MEDICAL INFO
              </div>
            </div>

            {/* Name & DOB */}
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-white mb-1">
                {medicalInfo.full_name || profile?.name || 'Name'}
              </h1>
              {medicalInfo.date_of_birth && (
                <p className="text-slate-400 flex items-center justify-center gap-2">
                  <Calendar className="h-4 w-4" />
                  DOB: {medicalInfo.date_of_birth}
                </p>
              )}
            </div>

            {/* Blood Type */}
            {summary?.blood_type && (
              <div className="bg-red-600 rounded-2xl p-4 mb-4 text-center">
                <div className="flex items-center justify-center gap-2 text-red-200 text-sm mb-1">
                  <Droplets className="h-4 w-4" />
                  BLOOD TYPE
                </div>
                <p className="text-4xl font-black text-white">{summary.blood_type}</p>
              </div>
            )}

            {/* Allergies */}
            <div className="bg-amber-500 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-2 text-amber-900 text-sm font-bold mb-2">
                <AlertTriangle className="h-4 w-4" />
                ALLERGIES
              </div>
              <p className="text-xl font-bold text-white">
                {summary?.allergies && summary.allergies.length > 0 
                  ? summary.allergies.join(', ')
                  : 'No Known Allergies'}
              </p>
            </div>

            {/* Medications */}
            <div className="bg-blue-600 rounded-2xl p-4 mb-4">
              <div className="flex items-center gap-2 text-blue-200 text-sm font-bold mb-2">
                <Pill className="h-4 w-4" />
                MEDICATIONS
              </div>
              <p className="text-lg font-bold text-white">
                {summary?.medications && summary.medications.length > 0 
                  ? summary.medications.join(', ')
                  : 'None'}
              </p>
            </div>

            {/* Emergency Contact */}
            {summary?.emergency_contact && (
              <div className="bg-emerald-600 rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-2 text-emerald-200 text-sm font-bold mb-2">
                  <Phone className="h-4 w-4" />
                  EMERGENCY CONTACT
                </div>
                <p className="text-lg font-bold text-white">{summary.emergency_contact}</p>
                {summary.emergency_contact_relationship && (
                  <p className="text-emerald-200 text-sm">({summary.emergency_contact_relationship})</p>
                )}
                {summary.emergency_contact_phone && (
                  <p className="text-2xl font-black text-white mt-1">{summary.emergency_contact_phone}</p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-6 pt-4 border-t border-slate-700">
              <p className="text-slate-500 text-xs">
                Created with Forget Me Not App
              </p>
            </div>
          </div>
        </div>

        {/* Download Button (Bottom) */}
        <Button
          onClick={handleDownload}
          disabled={generating}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
        >
          <Download className="h-5 w-5 mr-2" />
          {generating ? 'Generating Image...' : 'Download Lock Screen Card'}
        </Button>
      </main>
    </div>
  );
};

export default LockScreenCardPage;
